import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import Downloadicon from '../../../../../../media/billing/ic-download.svg'
import {Dispatch, SetStateAction, useEffect, useState} from 'react'
import AddSharedClassModal from './add-security-class-modal'
import {getShareSecurityClassById} from '../../core/_requests'
import {useAuth} from '../../../../auth'
import Skeleton from 'react-loading-skeleton'
import {
  formatNumber,
  getCurrencySymbol,
  getFileType,
  parseCurrency,
  toTitleCase,
} from '../../../../../core/_util'
import {useDateFormat} from '../../../../../hooks/useDateFormat'
import {
  ConveresionParameters,
  conversionParametersOptions,
  participatingOptions,
  seniorityOptions,
} from '../../core/_constants'
import {toast} from 'react-toastify'
import {downloadDocument} from '../../../founders-and-promoters/core/_requests'
import {useCurrency} from '../../../../../hooks/useCurrency'
import {NoDataPage} from '../../../../resources/components/NoDataPage'

export default function SecurityClassDetailsModal({
  isActive,
  setIsActive,
  shareClassId,
  setIsCreateOrUpdate,
  isCreateOrUpdate,
  setSidebar,
}: {
  isActive: boolean
  setIsActive: () => void
  shareClassId: string | null
  setIsCreateOrUpdate: Dispatch<SetStateAction<boolean>>
  isCreateOrUpdate: boolean
  setSidebar: Dispatch<SetStateAction<number>>
}) {
  const {formatMessage} = useIntl()
  const [shareClassDetailsLoading, setShareClassDetailsLoading] = useState<boolean>(false)
  const [shareSecurityClassDetails, setShareSecurityClassDetails] = useState<any>(null)
  const {companyCurrency, companyId} = useAuth()
  const {getDateValue} = useDateFormat()
  const [activeModal, setActiveModal] = useState<'none' | 'AddSecurityClassModal'>('none')
  const {currencyOptions} = useCurrency()

  const openModal = (modalType: 'AddSecurityClassModal') => {
    setActiveModal(modalType)
  }

  const closeModal = () => {
    setActiveModal('none')
  }

  const downloadFile = async (name: string) => {
    try {
      const {
        data: {success, data, errors},
      } = await downloadDocument(name, companyId!)
      if (success) {
        const link = document.createElement('a')
        link.href = data
        link.setAttribute('download', name)
        document.body.appendChild(link)
        toast.success('Document downloaded successfully')
        link.click()
        link.remove()
      } else {
        toast.error(errors[0])
      }
    } catch (error) {
      console.log(error)
    }
  }

  const fetchShareSecurityClassDetails = async () => {
    if (!shareClassId) return
    setShareClassDetailsLoading(true)
    try {
      const {
        data: {success, data},
      } = await getShareSecurityClassById(companyId!, shareClassId)
      if (success) {
        setShareSecurityClassDetails(data)
        setIsCreateOrUpdate(false)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setShareClassDetailsLoading(false)
    }
  }

  useEffect(() => {
    fetchShareSecurityClassDetails()
  }, [shareClassId, isCreateOrUpdate]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Modal
        show={isActive}
        onHide={() => {
          setIsActive()
        }}
        size='lg'
      >
        <Modal.Header closeButton>
          <h2>{formatMessage({id: 'Shared Class View'})}</h2>
        </Modal.Header>
        <Modal.Body className='container'>
          <div className='d-md-flex justify-content-between w-100 align-items-center mb-6'>
            <h1 className='fs-4'>{formatMessage({id: 'Basic Details'})}</h1>
            <div className='h-100 border bg-white d-flex align-items-center rounded'>
              <button
                className='btn btn-primary font-size-12 px-4 py-2 w-100 w-md-auto'
                title='edit transaction'
                onClick={() => {
                  openModal('AddSecurityClassModal')
                }}
              >
                {formatMessage({id: 'Edit Share Class'})}
              </button>
            </div>
          </div>

          <div className='row gx-6 justify-content-start mb-4'>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Class Type'})}</p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareSecurityClassDetails
                    ? toTitleCase(shareSecurityClassDetails?.shareType)
                    : '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Share Class name'})}
              </p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <div
                  style={{
                    backgroundColor: `rgba(${parseInt(
                      shareSecurityClassDetails?.tagColor.slice(1, 3),
                      16
                    )}, 
            ${parseInt(shareSecurityClassDetails?.tagColor.slice(3, 5), 16)}, 
            ${parseInt(shareSecurityClassDetails?.tagColor.slice(5, 7), 16)}, 
            0.4)`,
                    opacity: '0.8',
                    padding: '5px',
                    width: '70%',
                    borderRadius: '5px',
                  }}
                >
                  <p className='font-size-14 m-0 font-weight-normal'>
                    {shareSecurityClassDetails?.className}
                  </p>
                </div>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Share Class Code'})}
              </p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareSecurityClassDetails?.classCode ?? '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Currency'})}</p>
              <p className='font-size-14 mb-0'>{companyCurrency}</p>
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Date (First Closing)'})}
              </p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareSecurityClassDetails?.firstClosingDate
                    ? getDateValue(shareSecurityClassDetails?.firstClosingDate)
                    : '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Par (Face) Value'})}
              </p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareSecurityClassDetails?.parValue
                    ? `${companyCurrency} ${getCurrencySymbol(
                        companyCurrency,
                        currencyOptions
                      )} ${parseCurrency(shareSecurityClassDetails?.parValue)}`
                    : '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Price Per Share'})}
              </p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareSecurityClassDetails?.pricePerShare
                    ? `${companyCurrency} ${getCurrencySymbol(
                        companyCurrency,
                        currencyOptions
                      )} ${parseCurrency(shareSecurityClassDetails?.pricePerShare)}`
                    : '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Vote Per Share'})}
              </p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 m-0 font-weight-normal'>
                  {shareSecurityClassDetails?.votePerShare
                    ? `${formatNumber(shareSecurityClassDetails?.votePerShare)}`
                    : '-'}
                </p>
              )}
            </div>
          </div>

          <div className='seperator bg-dark border mb-6'></div>

          <div className='d-md-flex justify-content-between w-100 align-items-center mb-6'>
            <h1 className='fs-4'>{formatMessage({id: 'Liquidation Preferences'})}</h1>
          </div>
          <div className='row gx-6 justify-content-start mb-4'>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Seniority Level'})}
              </p>

              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 mb-0'>
                  {seniorityOptions.find(
                    (seniority) => seniority.value === shareSecurityClassDetails?.seniorityLevel
                  )?.label || '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Multiplier'})}</p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 mb-0'>
                  {shareSecurityClassDetails?.multiplier
                    ? `${formatNumber(shareSecurityClassDetails?.multiplier)}x`
                    : '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>{formatMessage({id: 'Participating'})}</p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 mb-0'>
                  {
                    participatingOptions.find(
                      (participate) =>
                        participate.value === shareSecurityClassDetails?.participating
                    )?.label||"-"
                  }
                </p>
              )}
            </div>
            {shareSecurityClassDetails?.participating === 'yes' && (
              <div className='col-lg-3 col-md-4 col-6 mb-8'>
                <p className='text muted font-size-12 mb-0'>
                  {formatMessage({id: 'Participation Cap'})}
                </p>
                {shareClassDetailsLoading ? (
                  <Skeleton
                    height={30}
                    width={120}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <p className='font-size-14 mb-0'>
                    {shareSecurityClassDetails?.participatingCap
                    ? `${formatNumber(shareSecurityClassDetails?.participatingCap)}x`
                    : '-'}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className='seperator bg-dark border mb-6'></div>

          <div className='d-md-flex justify-content-between w-100 align-items-center mb-6'>
            <h1 className='fs-4'>{formatMessage({id: 'Conversion Preferences'})}</h1>
          </div>
          <div className='row gx-6 justify-content-start mb-4'>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Conversion Parameters'})}
              </p>
              {shareClassDetailsLoading ? (
                <Skeleton
                  height={30}
                  width={120}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              ) : (
                <p className='font-size-14 mb-0'>
                  {conversionParametersOptions.find(
                    (conversion) =>
                      conversion.value === shareSecurityClassDetails?.conversionParameter
                  )?.label || '-'}
                </p>
              )}
            </div>
            <div className='col-lg-3 col-md-4 col-6 mb-8'>
              <p className='text muted font-size-12 mb-0'>
                {formatMessage({id: 'Conversion Ratio'})}
              </p>
              <p className='font-size-14 mb-0'>
                {shareClassDetailsLoading ? (
                  <Skeleton
                    height={30}
                    width={120}
                    baseColor='var(--kt-skeleton-bg-color)'
                    highlightColor='var(--kt-skeleton-highlight-bg-color)'
                  />
                ) : (
                  <>
                    {shareSecurityClassDetails?.conversionParameterValue == null
                      ? '-'
                      : shareSecurityClassDetails?.conversionParameter ===
                        ConveresionParameters.CONVERSION_RATIO
                      ? `${shareSecurityClassDetails.conversionParameterValue}x`
                      : `${companyCurrency} ${getCurrencySymbol(
                          companyCurrency,
                          currencyOptions
                        )} ${parseCurrency(shareSecurityClassDetails.conversionParameterValue)}`}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className='seperator bg-dark border mb-6'></div>

          <div className='d-md-flex justify-content-between w-100 align-items-center mb-6'>
            <h1 className='fs-4'>{formatMessage({id: 'Documents'})}</h1>
          </div>

          {shareClassDetailsLoading ? (
            <div className='d-flex gap-3'>
              <div className='card p-3 border border-muted'>
                <Skeleton
                  height={30}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />

                <Skeleton
                  height={60}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
              <div className='card p-3 border border-muted'>
                <Skeleton
                  height={30}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
                <Skeleton
                  height={60}
                  width={200}
                  baseColor='var(--kt-skeleton-bg-color)'
                  highlightColor='var(--kt-skeleton-highlight-bg-color)'
                />
              </div>
            </div>
          ) : (
            <div className='row'>
              {shareSecurityClassDetails?.documents.length === 0 ? (
                <NoDataPage />
              ) : (
                shareSecurityClassDetails?.documents.map((file: any) => (
                  <div className='col-12 col-md-6 col-lg-4 mb-4' key={file.documentId}>
                    <div className='card p-3 border border-muted'>
                      <img
                        src={getFileType(file.name)}
                        alt={file.name}
                        style={{height: '35px', width: '35px'}}
                      />
                      <div className='d-flex justify-content-between align-items-center'>
                        <p className='p-0 m-0 w-75'>{file?.name?.split('/').pop()}</p>
                        <img
                          className='cursor-pointer'
                          src={Downloadicon}
                          alt='download'
                          onClick={() => downloadFile(file.name)}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
      {activeModal === 'AddSecurityClassModal' && (
        <AddSharedClassModal
          isActive={true}
          setIsActive={closeModal}
          shareClassId={shareClassId}
          setIsCreateOrUpdate={setIsCreateOrUpdate}
          setSidebar={setSidebar}
        />
      )}
    </>
  )
}
